export function normalizeDuplicateFamilyCode(codFam) {
  return String(codFam || "").trim().toUpperCase() || null;
}

export function isFixedOnDuplicate(line) {
  return Boolean(line?.fijo_al_duplicar);
}

export function isKeepOriginPrice(line) {
  return Boolean(line?.mantener_precio_origen);
}

export function productCodeWithoutSeason(code, season) {
  const value = String(code || "").trim();
  const suffix = ` ${String(season || "").trim().toUpperCase()}`;
  return suffix !== " " && value.toUpperCase().endsWith(suffix)
    ? value.slice(0, -suffix.length).trimEnd() : value;
}

export function productCodeWithSeason(code, season, previousSeason = season) {
  const normalizedSeason = String(season || "").trim().toUpperCase();
  const base = productCodeWithoutSeason(code, previousSeason);
  return base && normalizedSeason ? `${base} ${normalizedSeason}` : "";
}

export function specialSizesProductCode(sourceCode, sourceSeason, targetSeason) {
  const base = productCodeWithoutSeason(sourceCode, sourceSeason);
  if (!base) return "";
  return targetSeason === undefined ? `${base}/E` : productCodeWithSeason(`${base}/E`, targetSeason);
}

export function preservedReferenceProductCode(sourceCode, family, season) {
  const code = String(sourceCode || "").trim();
  const initials = String(family?.iniciales?.trim() || family?.descrip?.trim().slice(0, 2) || "").toUpperCase();
  const normalizedSeason = String(season || "").trim().toUpperCase();
  return code && initials && normalizedSeason ? `${initials}-${code} ${normalizedSeason}` : "";
}

export function canPreserveDuplicateReference(sourceFamily, targetFamily) {
  const source = normalizeDuplicateFamilyCode(sourceFamily);
  return Boolean(source && source === normalizeDuplicateFamilyCode(targetFamily));
}

export function duplicateProcessKey(processId) {
  return processId || null;
}

export function duplicateMaterialKey(processId, materialId) {
  if (!processId || !materialId) return null;
  return `${processId}::${materialId}`;
}

export function templateProcessMap(template) {
  const map = new Map();
  for (const line of template?.process_lines || []) {
    const key = duplicateProcessKey(line.process_id);
    if (key && !map.has(key)) map.set(key, line);
  }
  return map;
}

export function templateMaterialMap(template) {
  const map = new Map();
  for (const process of template?.process_lines || []) {
    for (const child of process.child_lines || []) {
      const key = duplicateMaterialKey(process.process_id, child.material_id);
      if (key && !map.has(key)) map.set(key, { ...child, parent_process_id: process.process_id });
    }
  }
  return map;
}

export function sheetProcessMap(sheet) {
  const map = new Map();
  for (const line of sheet?.process_lines || []) {
    const key = duplicateProcessKey(line.process_id);
    if (key && !map.has(key)) map.set(key, line);
  }
  return map;
}

export function sheetMaterialMap(sheet) {
  const map = new Map();
  for (const process of sheet?.process_lines || []) {
    for (const material of process.material_lines || []) {
      const key = duplicateMaterialKey(process.process_id, material.material_id);
      if (key && !map.has(key)) map.set(key, { ...material, parent_process_id: process.process_id });
    }
  }
  return map;
}

export function processLineLabel(line, fallback = "Proceso") {
  return line?.process?.description || fallback;
}

export function materialLineLabel(line, processLine = null) {
  const material = line?.material;
  const name = material
    ? [material.code, material.internal_description].filter(Boolean).join(" · ")
    : "Material";
  const processName = processLineLabel(processLine, "");
  return processName ? `${name} (${processName})` : name;
}

export function destOnlyTemplateMaterials(destProcess, originProcess) {
  const originIds = new Set(
    (originProcess?.material_lines || []).map((line) => line.material_id).filter(Boolean)
  );
  return (destProcess?.child_lines || []).filter((child) => child.material_id && !originIds.has(child.material_id));
}

export function getDuplicateCostSheetMode({ sourceCodFam, destCodFam, destTemplate = null } = {}) {
  const source = normalizeDuplicateFamilyCode(sourceCodFam);
  const dest = normalizeDuplicateFamilyCode(destCodFam);
  if (!dest) return "copy";
  if (!destTemplate) return "copy";
  return "merge";
}

export function shouldRecalculateDuplicatePrices(sourceCodFam, destCodFam) {
  const source = normalizeDuplicateFamilyCode(sourceCodFam);
  const dest = normalizeDuplicateFamilyCode(destCodFam);
  return Boolean(dest);
}

export function shouldPreviewDuplicateFamilyChange(sourceCodFam, destCodFam) {
  const source = normalizeDuplicateFamilyCode(sourceCodFam);
  const dest = normalizeDuplicateFamilyCode(destCodFam);
  return Boolean(dest);
}

function issue(code, label, extra = {}) {
  return { code, label, ...extra };
}

export const DUPLICATE_FAMILY_RULES = {
  exclude: "Material marcado «Excluir al duplicar» en la plantilla destino: no aparece en el mismo proceso del duplicado aunque exista en el artículo origen. Tiene prioridad sobre Fijo, Tejido y Solo si existe en el artículo origen. No afecta al alta de artículos.",
  material: "Material marcado «Solo si existe en el artículo origen» en la plantilla destino: solo se incorpora si ya existe en el mismo proceso del artículo origen. Se aplica con y sin Fijo; desmarcado sigue la regla general. Tejido conserva el origen salvo los materiales excluidos expresamente en destino.",
  membership: "Mandan los procesos del artículo de origen: solo se incluyen si también existen en la plantilla destino. No se añaden procesos exclusivos del destino y se omiten los exclusivos del origen, aunque tengan Fijo o Tejido.",
  fixed: "Fijo: conserva el precio, la cantidad, las notas y los documentos del proceso de origen. En sus materiales conserva cantidades y mermas, actualiza los precios de los coincidentes con el destino y añade los que falten con los datos de destino. Los marcados Solo si existe en el artículo origen requieren existir en ese proceso; los marcados Excluir al duplicar se omiten siempre.",
  destination: "Sin Fijo ni Tejido: el proceso y sus materiales se toman íntegramente de la plantilla destino, incluidos precios y cantidades. Los marcados Solo si existe en el artículo origen se omiten si faltan en ese proceso; los marcados Excluir al duplicar se omiten siempre. Las líneas exclusivas del origen no se copian.",
  fabric: "Tejido: copia exactamente el proceso de origen, con sus materiales, cantidades, precios, mermas, notas y documentos. No añade ni modifica materiales; solo omite los marcados Excluir al duplicar en el mismo proceso de destino. Tiene prioridad sobre Fijo.",
};

function processOccurrences(lines = []) {
  const groups = new Map();
  const sorted = [...lines].sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)
    || String(a.id || "").localeCompare(String(b.id || "")));
  for (const line of sorted) {
    if (!groups.has(line.process_id)) groups.set(line.process_id, []);
    groups.get(line.process_id).push(line);
  }
  return groups;
}

function analyzeChangedFamilySheet(sourceTemplate, destTemplate, sourceSheet) {
  const issues = [];
  const rules = processOccurrences(sourceTemplate?.process_lines);
  const origins = processOccurrences(sourceSheet?.process_lines);
  const targets = processOccurrences(destTemplate.process_lines);
  if (!sourceTemplate) issues.push(issue("MISSING_SOURCE_RULES", "La familia origen no tiene plantilla: no hay marcas Fijo o Tejido que aplicar. Se aplicarán los datos de destino únicamente a los procesos que también existan en el artículo de origen."));
  for (const processId of new Set([...origins.keys(), ...targets.keys()])) {
    const sourceLines = origins.get(processId) || [];
    const targetLines = targets.get(processId) || [];
    const flags = rules.get(processId) || [];
    for (let index = 0; index < Math.max(sourceLines.length, targetLines.length); index++) {
      const origin = sourceLines[index];
      const target = targetLines[index];
      const rule = flags[index] || flags[0];
      const label = processLineLabel(origin || target);
      const extra = { kind: "process", processId, occurrence: index + 1 };
      const excludedIds = new Set((target?.child_lines || []).filter((m) => m.excluir_al_duplicar).map((m) => m.material_id));
      const allowedMaterials = (target?.child_lines || []).filter((material) => {
        if (excludedIds.has(material.material_id)) {
          if (origin) issues.push(issue("DEST_MATERIAL_EXCLUDED", materialLineLabel(material, target),
            { kind: "material", processId, materialId: material.material_id, occurrence: index + 1 }));
          return false;
        }
        const excluded = origin && !isKeepOriginPrice(rule) && material.solo_si_existe_destino
          && !(origin.material_lines || []).some((line) => line.material_id === material.material_id);
        if (excluded) issues.push(issue("DEST_MATERIAL_SKIPPED", materialLineLabel(material, target),
          { kind: "material", processId, materialId: material.material_id, occurrence: index + 1 }));
        return !excluded;
      });
      if (!origin) {
        issues.push(issue("DEST_PROCESS_SKIPPED", label, extra));
      } else if (!target) {
        issues.push(issue("SOURCE_PROCESS_REMOVED", label, extra));
      } else if (origin && isKeepOriginPrice(rule)) {
        issues.push(issue("FABRIC_COPIED_EXACTLY", label, extra));
      } else if (origin && isFixedOnDuplicate(rule)) {
        issues.push(issue("FIXED_PROCESS_PRESERVED", label, extra));
        for (const material of allowedMaterials) {
          const exists = (origin.material_lines || []).some((line) => line.material_id === material.material_id);
          issues.push(issue(exists ? "FIXED_MATERIAL_REPRICED" : "FIXED_MATERIAL_ADDED", materialLineLabel(material, target),
            { kind: "material", processId, materialId: material.material_id, occurrence: index + 1 }));
        }
      } else if (target) {
        issues.push(issue("DEST_PROCESS_REPLACES_ORIGIN", processLineLabel(target), extra));

      }
    }
  }
  const excludedLooseIds = new Set((destTemplate.orphan_material_lines || []).filter((m) => m.excluir_al_duplicar).map((m) => m.material_id));
  for (const material of destTemplate.orphan_material_lines || []) {
    if (excludedLooseIds.has(material.material_id)) {
      issues.push(issue("DEST_MATERIAL_EXCLUDED", materialLineLabel(material), { kind: "material", materialId: material.material_id }));
      continue;
    }
    if (material.solo_si_existe_destino && !(sourceSheet?.material_lines || []).some(
      (line) => !line.process_line_id && line.material_id === material.material_id
    )) issues.push(issue("DEST_MATERIAL_SKIPPED", materialLineLabel(material),
      { kind: "material", materialId: material.material_id }));
  }
  if (destTemplate.orphan_material_lines?.length || (sourceSheet?.material_lines || []).some((line) => !line.process_line_id)) {
    issues.push(issue("DEST_LOOSE_MATERIALS", "Materiales sin proceso: se toman de destino salvo los excluidos; los marcados requieren existir sin proceso en el artículo origen."));
  }
  return { mode: "merge", issues, recalculatePrices: true };
}

export function analyzeDuplicateCostSheet({
  sourceCodFam,
  destCodFam,
  sourceTemplate = null,
  destTemplate = null,
  sourceSheet = null,
} = {}) {
  const source = normalizeDuplicateFamilyCode(sourceCodFam);
  const dest = normalizeDuplicateFamilyCode(destCodFam);
  const issues = [];

  if (!destTemplate) {
    issues.push(
      issue(
        "MISSING_DEST_TEMPLATE",
        "La familia destino no tiene plantilla. Se copiará el escandallo tal cual."
      )
    );
    return { mode: "copy", issues, recalculatePrices: true };
  }

  if (source !== dest) return analyzeChangedFamilySheet(sourceTemplate, destTemplate, sourceSheet);

  const sourceTplProcesses = templateProcessMap(sourceTemplate);
  const destTplProcesses = templateProcessMap(destTemplate);
  const sourceSheetProcesses = sheetProcessMap(sourceSheet);

  if (!sourceTemplate) {
    issues.push(
      issue(
        "MISSING_SOURCE_TEMPLATE",
        "La familia origen no tiene plantilla. Se conservarán sus líneas y se completarán con la plantilla destino."
      )
    );
  }

  for (const [processId, line] of sourceTplProcesses) {
    const destHasProcess = destTplProcesses.has(processId);
    const originHasProcess = sourceSheetProcesses.has(processId);
    const fixed = isFixedOnDuplicate(line);

    if (fixed && !destHasProcess && originHasProcess) {
      issues.push(
        issue("FIXED_PROCESS_ADDED_FROM_ORIGIN", processLineLabel(line), {
          kind: "process",
          processId,
          fixed: true,
        })
      );
      continue;
    }

    if (fixed && destHasProcess && !originHasProcess) {
      issues.push(
        issue("FIXED_PROCESS_USES_DEST", processLineLabel(line), {
          kind: "process",
          processId,
          fixed: true,
        })
      );
      continue;
    }

    if (fixed && destHasProcess && originHasProcess) {
      const destProcess = destTplProcesses.get(processId);
      const originProcess = sourceSheetProcesses.get(processId);
      for (const child of destOnlyTemplateMaterials(destProcess, originProcess)) {
        issues.push(
          issue("DEST_MATERIAL_ADDED_TO_FIXED_PROCESS", materialLineLabel(child, destProcess), {
            kind: "material",
            processId,
            materialId: child.material_id,
          })
        );
      }
    }

    if (!fixed && !destHasProcess) {
      issues.push(
        issue("NON_FIXED_PROCESS_MISSING_IN_DEST", processLineLabel(line), {
          kind: "process",
          processId,
          fixed: false,
        })
      );
    }
  }

  for (const [processId, line] of destTplProcesses) {
    const originHasProcess = sourceSheetProcesses.has(processId);
    if (!originHasProcess) {
      issues.push(
        issue("DEST_PROCESS_CREATED", processLineLabel(line), { kind: "process", processId })
      );
      continue;
    }
    if (isKeepOriginPrice(line)) {
      issues.push(
        issue("ORIGIN_PRICE_KEPT_TEJIDO", processLineLabel(line), { kind: "process", processId })
      );
      continue;
    }
    {
      issues.push(
        issue("DEST_PRICES_APPLIED", processLineLabel(line), { kind: "process", processId })
      );
    }
  }

  for (const [processId, line] of sourceSheetProcesses) {
    if (sourceTplProcesses.has(processId)) continue;
    issues.push(
      issue("SOURCE_EXTRA_PROCESS", processLineLabel(line), { kind: "process", processId })
    );
  }

  return { mode: "merge", issues, recalculatePrices: true };
}

export const DUPLICATE_ISSUE_GROUPS = [
  { id: "materials-excluded", title: "Materiales excluidos por la familia destino", description: DUPLICATE_FAMILY_RULES.exclude, tone: "info", codes: ["DEST_MATERIAL_EXCLUDED"] },
  { id: "destination-only-processes", title: "Procesos de destino que no se añadirán", description: "No existen en el artículo de origen. La duplicación parte de sus procesos.", tone: "info", codes: ["DEST_PROCESS_SKIPPED"] },
  { id: "materials-skipped", title: "Materiales que no se añadirán", description: "Están marcados en la plantilla destino y faltan en el mismo proceso del artículo origen.", tone: "info", codes: ["DEST_MATERIAL_SKIPPED"] },
  { id: "source-rules", title: "Marcas de origen", tone: "warning", codes: ["MISSING_SOURCE_RULES"] },
  { id: "fixed-preserved", title: "Procesos fijos: precio y cantidad de origen", description: DUPLICATE_FAMILY_RULES.fixed, tone: "info", codes: ["FIXED_PROCESS_PRESERVED"] },
  { id: "fixed-material-prices", title: "Materiales de procesos fijos: precios de destino", description: "Se conservan las cantidades y mermas del origen.", tone: "info", codes: ["FIXED_MATERIAL_REPRICED"] },
  { id: "fixed-material-new", title: "Materiales que se añaden a procesos fijos", description: "Se incorporan con los datos de la plantilla destino.", tone: "info", codes: ["FIXED_MATERIAL_ADDED"] },
  { id: "fabric-exact", title: "Tejido: datos de origen y exclusiones de materiales", description: DUPLICATE_FAMILY_RULES.fabric, tone: "info", codes: ["FABRIC_COPIED_EXACTLY"] },
  { id: "destination-replacement", title: "Procesos y materiales de destino", description: DUPLICATE_FAMILY_RULES.destination, tone: "info", codes: ["DEST_PROCESS_REPLACES_ORIGIN"] },
  { id: "source-removed", title: "Procesos de origen que no se copiarán", description: "No existen en la plantilla destino. No se copian aunque tengan Fijo o Tejido.", tone: "warning", codes: ["SOURCE_PROCESS_REMOVED"] },
  { id: "loose-materials", title: "Materiales sin proceso", tone: "info", codes: ["DEST_LOOSE_MATERIALS"] },
  {
    id: "missing-templates",
    title: "Plantillas",
    tone: "warning",
    codes: ["MISSING_DEST_TEMPLATE", "MISSING_SOURCE_TEMPLATE"],
  },
  {
    id: "fixed-added",
    title: "Procesos fijos que se añadirán desde el artículo origen",
    description: "No están en la plantilla destino; se copian enteros del origen (materiales, costes, notas y documentos).",
    tone: "info",
    codes: ["FIXED_PROCESS_ADDED_FROM_ORIGIN"],
  },
  {
    id: "fixed-uses-dest",
    title: "Procesos fijos que el origen no tiene",
    description: "Se deja el proceso de la plantilla destino.",
    tone: "warning",
    codes: ["FIXED_PROCESS_USES_DEST"],
  },
  {
    id: "not-transferred",
    title: "Procesos adicionales que se conservan del origen",
    description: "Se conservan con sus materiales y documentos aunque no estén en la plantilla destino.",
    tone: "warning",
    codes: ["NON_FIXED_PROCESS_MISSING_IN_DEST", "SOURCE_EXTRA_PROCESS"],
  },
  {
    id: "dest-created",
    title: "Procesos que se crearán desde la plantilla destino",
    description: "No están en el artículo origen; se añaden con precios de la familia destino.",
    tone: "info",
    codes: ["DEST_PROCESS_CREATED", "DEST_PROCESS_NOT_IN_SOURCE"],
  },
  {
    id: "dest-prices",
    title: "Precios que se actualizan a la plantilla destino",
    description: "El proceso se conserva del origen y los precios pasan a los de la familia destino.",
    tone: "info",
    codes: ["DEST_PRICES_APPLIED"],
  },
  {
    id: "tejido-prices",
    title: "Precios que se mantienen del origen",
    description: "Procesos marcados como tejido / no actualizar precio.",
    tone: "warning",
    codes: ["ORIGIN_PRICE_KEPT_TEJIDO"],
  },
  {
    id: "dest-materials-on-fixed",
    title: "Materiales de marca que se añadirán a procesos fijos",
    description: "El proceso se copia del origen y se suman estos materiales de la plantilla destino (por ejemplo una bolsa).",
    tone: "info",
    codes: ["DEST_MATERIAL_ADDED_TO_FIXED_PROCESS"],
  },
];

export function groupDuplicateIssues(issues = []) {
  return DUPLICATE_ISSUE_GROUPS.map((group) => ({
    ...group,
    items: issues.filter((item) => group.codes.includes(item.code)),
  })).filter((group) => group.items.length > 0);
}

export function formatFamilyTemplateOption(template) {
  const code = template?.family?.cod_fam?.trim() || template?.cod_fam?.trim();
  const descrip = template?.family?.descrip;
  if (code && descrip) return `${code} · ${descrip}`;
  if (code) return `${code} · ${template?.name || "Plantilla"}`;
  return template?.name || "Plantilla";
}
